import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  RecordData,
  RecordTableColumnParams,
  RecordUpdateParams
} from "../../../../../types/records-table.types";
import {Pageable} from "../../../../../types/pagable.types";
import {RecordTableAction, TableActionType} from "../../../../../types/record-table-action.types";
import {ResourceParams} from "../../../../../models/resource/resources";
import {
  headerFooterHeight,
  headerFooterHeightWithoutGroup
} from "../../../configs/records-table-config";
import {defaultResourceParams} from "../../../../../models/resource/default-resource-params";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {UtilityService} from "../../../../../services/utility.service";
import {ColumnParamsStateService} from "../../../../../services/table-services/column-params-state.service";
import {TableColumnCheckboxService} from "../../../../records-table-container/services/table-column-checkbox.service";
import {RecordsTableService} from "../../../../../services/records-table.service";
import {ModalsService} from "../../../../../services/modals.service";
import {ColumnResizeService} from "../../../../../services/table-services/column-resize.service";
import {TableBodyService} from "../../../../../services/table-services/table-body.service";
import {SelectedRowStateService} from "../../../../../services/table-services/selected-row-state.service";
import {ModalStatesService} from "../../../../../services/table-services/modal-states.service";
import {
  WrapperElementSizeStateService
} from "../../../../../services/table-services/wrapper-element-size-state.service";
import {defaultHeightParams} from "../../../../records-table-container/configs/record-table-container-config";
import {TableActionsService} from "../../../services/table-actions.service";
import {TableAction} from "../../../types/table-actions.types";
import {DEFAULT_ZOOM} from "../../../../../constants/constants";
import {TableContextService} from "../../../../../services/table-services/table-context.service";

@Component({
  selector: 'app-table-body',
  standalone: false,
  templateUrl: './table-body.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: [
    '../../../records-table-view.component.scss',
    './table-body.component.scss'
  ]
})
export class TableBodyComponent implements AfterViewInit, OnChanges {
  @Input({ required: true }) currentTableZoom: number = DEFAULT_ZOOM;
  @Input({ required: true }) tableData: Pageable<RecordData> | null = null;
  @Input({ required: true }) isEditModeActive: boolean = false;
  @Input({ required: true }) tableActions: Array<RecordTableAction> = [];
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Input() isAnyTableActionAvailable: boolean = false;
  @Input() isNeedGroups: boolean = true;
  @Input() isAddNewRecordStateActive: boolean = false;
  @Output() recordUpdated: EventEmitter<RecordUpdateParams> = new EventEmitter<RecordUpdateParams>();
  @Output() reloadTableData: EventEmitter<void> = new EventEmitter<void>();
  @Output() recordDeleted: EventEmitter<string | number> = new EventEmitter<string | number>();
  @Output() handleNewRecord: EventEmitter<Partial<RecordData>> = new EventEmitter<Partial<RecordData>>();
  @ViewChild(CdkVirtualScrollViewport, { static: false }) public viewPort!: CdkVirtualScrollViewport;
  @ViewChildren('bodyCell') bodyCells!: QueryList<ElementRef<HTMLElement>>;

  public columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);
  public selectedRowStateService: SelectedRowStateService = inject(SelectedRowStateService);
  private utilityService: UtilityService = inject(UtilityService);
  private tableColumnCheckBoxService: TableColumnCheckboxService = inject(TableColumnCheckboxService);
  private recordsTableService: RecordsTableService = inject(RecordsTableService);
  private modalStatesService: ModalStatesService = inject(ModalStatesService);
  private modalService: ModalsService = inject(ModalsService);
  private columnResizeService: ColumnResizeService = inject(ColumnResizeService);
  private tableBodyService: TableBodyService = inject(TableBodyService);
  private wrapperElementSizeStateService: WrapperElementSizeStateService = inject(WrapperElementSizeStateService);
  private tableActionsService: TableActionsService = inject(TableActionsService);
  private tableContextService: TableContextService = inject(TableContextService);

  public trackByRecordId = (index: number, record: RecordData): string | number => {
    return record.id;
  };

  public get getBodyGroupRowHeight(): string {
    return !this.tableData?.content.length && !this.isAddNewRecordStateActive ? `calc(${this.getHeight()} - 10px)` : '0';
  }

  public get inverseOfTranslation(): number {
    const offset = this.viewPort?.['_renderedContentOffset'];
    return offset ? -offset : 0;
  }

  public ngAfterViewInit() {
    this.registerAllCells();
    this.bodyCells.changes.subscribe(() => this.registerAllCells());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    changes['currentTableZoom'] && this.scheduleViewportUpdate();
  }

  public handleNewRecordDataChange(newRecordData: Partial<RecordData>): void {
    this.handleNewRecord.emit(newRecordData);
  }

  public getRowColor(rowColor?: string | null): string {
    if (!rowColor) { return  ''; }

    return this.utilityService.hexToRgba(rowColor, 0.1);
  }

  public handleRowClick(rowId: number | string): void {
    this.selectedRowStateService.handleRowClick(Number(rowId), this.isEditModeActive);
  }

  public isNeedCheckbox(index: number): boolean {
    return this.tableActions.includes('CREATE_BLOGGER_GROUPS') && index === 0;
  }

  public getChecked(id?: number | string): boolean | undefined {
    return id ?
      this.tableColumnCheckBoxService.columnCheckboxState().subtasks.find(i => i.id === id)?.completed :
      this.tableColumnCheckBoxService.columnCheckboxState().completed;
  }

  public updateChecked(completed: boolean, id?: number | string): void {
    this.tableColumnCheckBoxService.updateColumnCheckboxState({
      id: Number(id),
      completed: completed
    });
  }

  public isColumnDisplayed(columnParams: RecordTableColumnParams): boolean {
    return this.recordsTableService.isColumnDisplayed(columnParams);
  }

  public isColumnWithBackground(columnParams: RecordTableColumnParams, recordData: RecordData): boolean {
    return this.tableBodyService.isColumnWithBackground(columnParams, recordData);
  }

  public getWidth(columnParam: RecordTableColumnParams): number {
    return this.recordsTableService.getColumnWidth(columnParam, this.tableContextService.getTableId());
  }

  public handleRecordUpdate(recordUpdateParams: RecordUpdateParams): void {
    this.recordUpdated.emit(recordUpdateParams);
  }

  public handleReloadTableData(): void {
    this.reloadTableData.emit();
  }

  public getIconMistakeMessageClass(key: string) {
    return key === 'id' ? 'info-id-icon' : 'info-icon';
  }

  public getMistakeMessageForCell(columnParams: RecordTableColumnParams, recordData: RecordData): string | null {
    return this.tableBodyService.getMistakeMessageForCell(columnParams, recordData);
  }

  public isAdvertisementPackage(columnKey: string, recordData: RecordData): boolean {
    return !!recordData['advertisementPackage'] && (columnKey === 'id' || columnKey === 'recordCalendarId') && !this.modalStatesService.hasOpenModals();
  }

  public async handlePackageClick(columnKey: string, recordData: RecordData): Promise<void> {
    await this.modalService.openAdvertisementPackageModal(columnKey, recordData);
  }

  public getViewportStyle(): Record<string, string> {
    return {
      'margin-right': this.hasVerticalScroll() ? '0' : '10px'
    };
  }

  public getActions(record: RecordData): Array<TableAction> {
    return this.tableActionsService.getActions({
      tableActions: this.tableActions,
      resourcePath: this.resourceParams.path,
      record: record
    });
  }

  public async handleActionClick(actionType: TableActionType, recordData: RecordData): Promise<void> {
    const actionHandlers: Record<string, (recordData: RecordData) => Promise<void> | void> = {
      'TELEGRAM_ATTACH_ALL': () => this.openTelegramAttachAll(recordData),
      'CHANGE_USER_PASSWORD': () => this.openChangePasswordModal(recordData),
      'BLOGGER_DEDUPLICATION': () => this.openBloggerDeduplicationModal(recordData),
      'DELETE_ROW': () => this.handleRecordDelete(recordData.id)
    };

    const handler = actionHandlers[actionType];
    handler && await handler(recordData);
  }

  private registerAllCells() {
    this.bodyCells.forEach(ref => {
      const key = ref.nativeElement.getAttribute('data-column')!;
      this.columnResizeService.registerElement(key, ref.nativeElement);
    });
  }

  private scheduleViewportUpdate() {
    requestAnimationFrame(() => this.viewPort?.checkViewportSize());
  }

  private hasVerticalScroll(): boolean {
    if (!this.viewPort) { return false; }

    const viewportSize = this.viewPort.getViewportSize();
    const contentSize = this.viewPort.getDataLength() * 50;

    return contentSize > viewportSize;
  }

  private async openTelegramAttachAll(recordData: RecordData): Promise<void> {
    await this.modalService.openTelegramAttachAllModal(Number(recordData.id), recordData['login']);
    this.reloadTableData.emit();
  }

  private openChangePasswordModal(recordData: RecordData): void {
    this.modalService.openChangePasswordModal(recordData);
  }

  private async openBloggerDeduplicationModal(recordData: RecordData): Promise<void> {
    await this.modalService.openBloggerDeduplicationModal(recordData);
    this.reloadTableData.emit();
  }

  private async handleRecordDelete(recordId: string | number): Promise<void> {
    this.recordDeleted.emit(recordId);
  }

  private getHeight(): string {
    const height = this.isNeedGroups ? headerFooterHeight : headerFooterHeightWithoutGroup;
    const isNotFullHeight = this.modalStatesService.hasOpenModals() && this.wrapperElementSizeStateService.isNonFullHeightTables(this.resourceParams.path);
    return isNotFullHeight ?
      `calc(${defaultHeightParams.maxHeightSizeTable / (this.currentTableZoom / 100)}px - ${height})`:
      `calc(${this.wrapperElementSizeStateService.getWrapperElementSize(this.resourceParams.path)?.height} - ${height})`;
  }
}
